import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { MemoryRouter, Route, Routes } from 'react-router-dom';

import CarbonProvider from 'carbon-react/lib/components/carbon-provider';
import { sageTheme } from 'carbon-react/lib/style/themes';
import TokensWrapper from 'carbon-react/lib/components/tokens-wrapper';

import { LocalisationProvider } from '@futrli/localisation';
import { LocalisedConstantsContextProvider } from '@futrli/localisation/core/localised-constants-context';
import { AiInsightsContainer } from '@futrli/predict/ai-insights/ai-insights-container';
import { fetchLiveReportById } from '@futrli/predict/core/api/reporting/report-builder';
import { SyncContextInternalProvider } from '@futrli/predict/core/sync-context/sync-context-core';
import { DEFAULT_SYNC_CONTEXT } from '@futrli/predict/core/sync-context/utils';
import { StandaloneReportContainer } from '@futrli/predict/report-builder/containers/standalone-report-container/standalone-report-container';
import { getOrgByBusinessId, getOrgById } from '@futrli/shared-services';
import { UIComponentsProvider } from '@futrli/ui-components';
import { LiveReportDetail } from '@futrli-sdk/predict';

import { fetchWorkspacesData } from '../workspaces/api';
import { LDProvider } from '../core/providers/launch-darkly';
import { store } from '../core/redux-utils/store';

export interface AppRenderWidgetProps {
  businessId: string;
  widgetName: string;
  /** Required when widgetName is "report". */
  reportId?: string;
}

interface WidgetData {
  key: string;
  workspaceId?: string;
  organisationId?: string;
  report?: LiveReportDetail;
  error?: string;
}

const fetchWidgetData = async ({ businessId, widgetName, reportId }: AppRenderWidgetProps) => {
  if (widgetName === 'report' && !reportId) {
    throw new Error('A report ID is required for the report widget');
  }
  const workspaces = await fetchWorkspacesData();
  let { organisation, workspace } = getOrgByBusinessId(workspaces, businessId);
  if (!organisation) {
    ({ organisation, workspace } = getOrgById(workspaces, businessId));
  }
  if (!organisation || !workspace) {
    throw new Error(`No organisation or workspace found for businessId: ${businessId}`);
  }
  const report = widgetName === 'report' && reportId
    ? await fetchLiveReportById(workspace.id, organisation.id, reportId)
    : undefined;
  return { workspaceId: workspace.id, organisationId: organisation.id, report };
};

export const AppRenderWidget = ({ businessId, widgetName, reportId }: AppRenderWidgetProps) => {
  const key = JSON.stringify([businessId, widgetName, reportId]);
  const [data, setData] = useState<WidgetData>();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const resolved = await fetchWidgetData({ businessId, widgetName, reportId });
        if (!cancelled) {
          setData({ key, ...resolved });
        }
      } catch (error) {
        if (!cancelled) {
          setData({ key, error: error instanceof Error ? error.message : 'Failed to load the Futrli widget' });
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [businessId, widgetName, reportId, key]);

  // Hide previous content immediately when the business or report changes.
  if (!data || data.key !== key) {
    return null;
  }
  if (data.error) {
    return <div role="alert">{data.error}</div>;
  }

  const { workspaceId, organisationId, report } = data;
  if (!workspaceId || !organisationId) {
    return null;
  }
  if (widgetName !== 'ai-insights' && widgetName !== 'report') {
    return <div>{'Widget not found'}</div>;
  }

  const initialPath = `/workspaces/${workspaceId}/organisations/${organisationId}/predict/${
    report ? `reporting/${report.id}/edit` : ''
  }`;

  return (
    <LDProvider>
      <MemoryRouter key={key} initialEntries={[initialPath]} initialIndex={0}>
        <Provider store={store}>
          <LocalisationProvider>
            <LocalisedConstantsContextProvider>
              <SyncContextInternalProvider value={DEFAULT_SYNC_CONTEXT}>
                <UIComponentsProvider>
                  <CarbonProvider theme={sageTheme}>
                    <TokensWrapper>
                      <Routes>
                        <Route
                          path="/workspaces/:workspaceId/organisations/:organisationId/predict/*"
                          element={report ? (
                            <StandaloneReportContainer
                              key={report.id}
                              workspaceId={workspaceId}
                              organisationId={organisationId}
                              report={report}
                            />
                          ) : <AiInsightsContainer />}
                        />
                      </Routes>
                    </TokensWrapper>
                  </CarbonProvider>
                </UIComponentsProvider>
              </SyncContextInternalProvider>
            </LocalisedConstantsContextProvider>
          </LocalisationProvider>
        </Provider>
      </MemoryRouter>
    </LDProvider>
  );
};
